import React, { useEffect } from "react";
import { centeringBox } from "components/Container";
import { Box } from "@chakra-ui/react";
import { logError } from "api/accounts-customer";
import { TiliaLogo } from "components/Logos";
import { SessionStoragePermissionError } from "utils/useSessionStorage";
import { RedirectNotAllowedError } from "utils/redirect";
import { inIframe, useSessionCtx, useSessionJWT, usePostMessage } from "utils";
import { useLocation } from "react-router-dom";

const getFlow = (location) => {
  const qsParams = new URLSearchParams(location.search);
  return qsParams.get("flow");
};

const Poster = ({ error, ctx = {}, sessionData = {}, sessionCtx = {} }) => {
  const location = useLocation();
  const { postOnError } = usePostMessage(getFlow(location) || "", sessionCtx?.intCtx);
  useEffect(() => {
    if (error) {
      logError(ctx, {
        flowLoc: "GAB",
        ...sessionData,
        ...sessionCtx,
        error: error.message,
      });
    }

    if (error && inIframe()) {
      postOnError({ message: error.message });
    }
  }, [postOnError, error, sessionData, sessionCtx, ctx]);
  return null;
};

const PosterWithCtx = ({ error }) => {
  const { sessionCtx } = useSessionCtx();
  const { sessionJWT, sessionData } = useSessionJWT();
  return (
    <Poster error={error} ctx={sessionJWT} sessionCtx={sessionCtx} sessionData={sessionData} />
  );
};

/**
 * Used to post error in the case of SessionStoragePermissionError.
 * Any other errors would go to the 'Poster' function above.
 * Since the Poster function above attempts to access sessionStorage, if we
 * reach this ErrorBoundary as a result of SessionStoragePermissionError, using that
 * function would cause a throw within the ErrorBoundary itself, which is a no-no
 */
const PosterWithoutSessionData = ({ error }) => {
  const { stack, ...rest } = error; // no need to send full stack
  return <Poster error={rest} />;
};

export class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  // componentDidCatch(error) {} // was used for newrelic reporting, just holding on incase we want to send this elsewhere

  render() {
    if (this.state.hasError) {
      const isStoragePermissionErr = this.state.error instanceof SessionStoragePermissionError;
      const isRedirectNotAllowedErr = this.state.error instanceof RedirectNotAllowedError;
      let additionalMessage = null;
      if (isStoragePermissionErr) {
        additionalMessage = (
          <div>You need to enable third-party cookies in order to use Tilia Pay.</div>
        );
      } else if (isRedirectNotAllowedErr) {
        additionalMessage = <div>Invalid redirect domain.</div>;
      }

      return (
        <Box
          data-testid="GAB"
          sx={{
            ...centeringBox,
            boxSizing: "border-box",
            flexDirection: "column",
            padding: "16px",
            textAlign: "center",
            "& > *": {
              marginBottom: "1.5em",
            },
            ...(isStoragePermissionErr ? { backgroundColor: "#ffffff" } : {}),
          }}
        >
          <TiliaLogo
            style={{
              height: "5vw",
              maxHeight: "36px",
              minHeight: "20px",
              marginBottom: "16px",
            }}
          />
          <div>Apologies, there has been an error.</div>
          {additionalMessage}
          {!isStoragePermissionErr ? (
            <PosterWithCtx error={this.state.error} />
          ) : (
            <PosterWithoutSessionData error={this.state.error} />
          )}
        </Box>
      );
    }
    return this.props.children;
  }
}
